import { getDb, StoredPacket } from './db';

/**
 * Triggers a browser file download of arbitrary string content.
 *
 * @param content - File contents string
 * @param filename - Output file name
 * @param mimeType - MIME type header
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Exports all georeferenced packets of a session (or all sessions) as a GeoJSON FeatureCollection.
 * Compatible with QGIS, ArcGIS, and Leaflet.
 *
 * @param sessionId - Optional session ID filter
 */
export async function exportGeoJSON(sessionId?: string): Promise<void> {
  const db = await getDb();
  let packets: StoredPacket[];

  if (sessionId) {
    packets = await db.getAllFromIndex('packets', 'by-session', sessionId);
  } else {
    packets = await db.getAll('packets');
  }

  // Filter to packets with valid coordinates
  const geoPackets = packets.filter((p) => p.hasGeo && p.lat !== null && p.lon !== null);

  const features = geoPackets.map((p) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [p.lon, p.lat, 0]
    },
    properties: {
      id: p.id,
      timestamp: new Date(p.timestamp).toISOString(),
      snr: p.snr,
      rssi: p.rssi,
      from: p.fromHex,
      fromName: p.fromName,
      to: p.toHex,
      relay: p.relayName,
      hopsTaken: p.hopsTaken,
      port: p.portName,
      summary: p.payloadSummary,
      accuracy: p.accuracy
    }
  }));

  const geoJson = {
    type: 'FeatureCollection',
    name: 'NodeRadar_Export',
    features
  };

  const filename = `noderadar_export_${Date.now()}.geojson`;
  downloadFile(JSON.stringify(geoJson, null, 2), filename, 'application/geo+json');
}

/**
 * Exports packets as a CSV spreadsheet conforming to RFC 4180.
 *
 * @param sessionId - Optional session ID filter
 */
export async function exportCSV(sessionId?: string): Promise<void> {
  const db = await getDb();
  let packets: StoredPacket[];

  if (sessionId) {
    packets = await db.getAllFromIndex('packets', 'by-session', sessionId);
  } else {
    packets = await db.getAll('packets');
  }

  const headers = [
    'id',
    'timestamp_iso',
    'latitude',
    'longitude',
    'accuracy_m',
    'snr_db',
    'rssi_dbm',
    'from_hex',
    'from_name',
    'to_hex',
    'relay_node',
    'relay_name',
    'hops_taken',
    'portnum',
    'port_name',
    'summary'
  ];

  const escapeCsv = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = packets.map((p) => [
    p.id,
    new Date(p.timestamp).toISOString(),
    p.lat ?? '',
    p.lon ?? '',
    p.accuracy ?? '',
    p.snr,
    p.rssi,
    p.fromHex,
    p.fromName,
    p.toHex,
    p.relayNode ? `0x${(p.relayNode & 0xff).toString(16)}` : '',
    p.relayName,
    p.hopsTaken,
    p.portnum,
    p.portName,
    p.payloadSummary
  ]);

  const csvContent = [headers.join(','), ...rows.map((r) => r.map(escapeCsv).join(','))].join(
    '\r\n'
  );

  const filename = `noderadar_export_${Date.now()}.csv`;
  downloadFile(csvContent, filename, 'text/csv');
}

/**
 * Exports packets as a Keyhole Markup Language (KML) document for Google Earth.
 *
 * @param sessionId - Optional session ID filter
 */
export async function exportKML(sessionId?: string): Promise<void> {
  const db = await getDb();
  let packets: StoredPacket[];

  if (sessionId) {
    packets = await db.getAllFromIndex('packets', 'by-session', sessionId);
  } else {
    packets = await db.getAll('packets');
  }

  const geoPackets = packets.filter((p) => p.hasGeo && p.lat !== null && p.lon !== null);

  const placemarks = geoPackets
    .map(
      (p) => `
    <Placemark>
      <name>${escapeXml(p.fromName)} (SNR: ${p.snr}dB)</name>
      <description><![CDATA[
        <b>Time:</b> ${new Date(p.timestamp).toLocaleString()}<br/>
        <b>SNR:</b> ${p.snr} dB<br/>
        <b>RSSI:</b> ${p.rssi} dBm<br/>
        <b>Sender:</b> ${p.fromName} (${p.fromHex})<br/>
        <b>Relay:</b> ${p.relayName}<br/>
        <b>Hops:</b> ${p.hopsTaken}<br/>
        <b>Port:</b> ${p.portName}<br/>
        <b>Summary:</b> ${p.payloadSummary}
      ]]></description>
      <Point>
        <coordinates>${p.lon},${p.lat},0</coordinates>
      </Point>
    </Placemark>`
    )
    .join('');

  const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>NodeRadar Track</name>
    ${placemarks}
  </Document>
</kml>`;

  const filename = `noderadar_export_${Date.now()}.kml`;
  downloadFile(kml, filename, 'application/vnd.google-earth.kml+xml');
}

function escapeXml(unsafe: string): string {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case '\'':
        return '&apos;';
      case '"':
        return '&quot;';
      default:
        return c;
    }
  });
}
